import { useEffect } from "react";

interface Props {
  title: string;
  body: string;
  confirmLabel?: string;
  /** "danger" (red, default) for deletes; "primary" (blue) for neutral confirmations. */
  tone?: "danger" | "primary";
  /** false: only the two buttons close it — for a choice that must be made. */
  dismissible?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function DeleteConfirm({
  title,
  body,
  confirmLabel = "Удалить",
  tone = "danger",
  dismissible = true,
  onConfirm,
  onCancel,
}: Props) {
  const confirmClass =
    tone === "primary"
      ? "bg-blue-500 hover:bg-blue-600"
      : "bg-red-600 hover:bg-red-700";
  useEffect(() => {
    if (!dismissible) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, dismissible]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={dismissible ? onCancel : undefined}
      data-testid="delete-confirm-backdrop"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl p-6 max-w-sm w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{body}</p>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5"
          >
            Отмена
          </button>
          <button
            onClick={onConfirm}
            className={`text-sm ${confirmClass} text-white px-3 py-1.5 rounded-md font-medium`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
